import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card,
  Row,
  Col,
  Table,
  Spinner,
  Alert,
  Tabs,
  Tab
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers,
  faUserShield,
  faSignInAlt,
  faHistory
} from '@fortawesome/free-solid-svg-icons';
import ApiService from '../../helpers/apiService';
import { IDashboardStats, IDashboardResponse, IAuditLog } from './types';
import TasksKanban from '../backlog/TasksKanban';
import GitlabTasksTab from '../backlog/GitlabTasksTab';
import VpmTab from '../backlog/VpmTab';

const REFRESH_INTERVAL_MS = 60_000;

interface StatCardProps {
  title: string;
  value: number;
  icon: any;
  bg: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, bg }) => (
  <Card className={`text-white bg-${bg} mb-3`}>
    <Card.Body className="d-flex justify-content-between align-items-center">
      <div>
        <div className="fs-6 fw-light">{title}</div>
        <div className="fs-3 fw-bold">{value}</div>
      </div>
      <FontAwesomeIcon icon={icon} size="2x" className="opacity-50" />
    </Card.Body>
  </Card>
);

const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleString('ru-RU');
  } catch {
    return dateStr;
  }
};

const OverviewTab: React.FC = () => {
  const [stats, setStats] = useState<IDashboardStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const errorCountRef = useRef(0);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await ApiService.get<IDashboardResponse>(
        '/dashboard/v1/stats'
      );
      const data = response?.data ?? {
        operators: 0,
        roles: 0,
        active_sessions: 0,
        actions_today: 0,
        recent_activity: []
      };
      setStats(data);
      errorCountRef.current = 0;
    } catch (err: any) {
      errorCountRef.current++;
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.message ?? err?.message ?? '';

      if (status === 404) {
        setError('Эндпоинт /dashboard/v1/stats не найден. Необходимо задеплоить обновлённый admin-api.');
      } else {
        const isServiceUnavailable =
          status === 500 &&
          /refused|dashboard service|rpc error/i.test(String(msg));
        setError(
          isServiceUnavailable
            ? 'Сервис статистики временно недоступен.'
            : msg || 'Не удалось загрузить статистику.'
        );
      }

      if (errorCountRef.current >= 2) {
        setAutoRefresh(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchStats, REFRESH_INTERVAL_MS);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [autoRefresh, fetchStats]);

  const recentActivity: IAuditLog[] = stats?.recent_activity ?? [];

  return (
    <>
      {error && (
        <Alert variant="warning" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && !stats ? (
        <div className="text-center py-5">
          <Spinner animation="border" /> <span>Загрузка...</span>
        </div>
      ) : (
        <>
          <Row>
            <Col md={3}>
              <StatCard
                title="Операторы"
                value={stats?.operators ?? 0}
                icon={faUsers}
                bg="primary"
              />
            </Col>
            <Col md={3}>
              <StatCard
                title="Роли"
                value={stats?.roles ?? 0}
                icon={faUserShield}
                bg="success"
              />
            </Col>
            <Col md={3}>
              <StatCard
                title="Активные сессии"
                value={stats?.active_sessions ?? 0}
                icon={faSignInAlt}
                bg="info"
              />
            </Col>
            <Col md={3}>
              <StatCard
                title="Действий за 24ч"
                value={stats?.actions_today ?? 0}
                icon={faHistory}
                bg="warning"
              />
            </Col>
          </Row>

          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>Последние действия</span>
              {loading && (
                <Spinner animation="border" size="sm" className="ms-2" />
              )}
            </Card.Header>
            <Card.Body className="p-0">
              {recentActivity.length === 0 ? (
                <div className="text-center text-muted py-4">
                  Нет недавних действий
                </div>
              ) : (
                <Table striped bordered hover responsive size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>Дата</th>
                      <th>Действие</th>
                      <th>Ресурс</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentActivity.map((log, idx) => (
                      <tr key={log.id ?? idx}>
                        <td>{formatDate(log.date)}</td>
                        <td>{log.action}</td>
                        <td>{log.resource}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </>
      )}
    </>
  );
};

const DashboardPage: React.FC = () => (
  <>
    <Card className="mb-3">
      <Card.Body>
        <h3 className="mb-0">Панель управления</h3>
        <p className="mt-2 mb-0">Статистика, продуктовый бэклог и VPM</p>
      </Card.Body>
    </Card>
    {/* Сведение (2026-06-30): «Бэклог»+«Задачи»+«GitLab» перекрывались. Канонический
        источник задач — GitLab Work Items (вкладка «Задачи»). Локальная таблица
        backlog_items (оперативные/черновики) — одна вкладка «Локальный трекер» (kanban);
        прежний список «Бэклог» (та же таблица) убран. */}
    <Tabs defaultActiveKey="overview" id="dashboard-tabs" className="mb-3">
      <Tab eventKey="overview" title="Обзор"><OverviewTab /></Tab>
      <Tab eventKey="tasks" title="Задачи (GitLab)"><GitlabTasksTab /></Tab>
      <Tab eventKey="local" title="Локальный трекер"><TasksKanban /></Tab>
      <Tab eventKey="vpm" title="VPM"><VpmTab /></Tab>
    </Tabs>
  </>
);

export default DashboardPage;
